import { useState, useRef, useEffect } from "react";
import { FileText, Download, Loader2, Calendar, Image as ImageIcon, BarChart3 } from "lucide-react";
import { toPng } from "html-to-image";
import { API_BASE, api, todayISO } from "../lib/api";
import { toast } from "sonner";
import FridgeChart from "../components/FridgeChart";

function startOfMonth() {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

export default function Report() {
    const [start, setStart] = useState(startOfMonth());
    const [end, setEnd] = useState(todayISO());
    const [includeCharts, setIncludeCharts] = useState(true);
    const [includeYearly, setIncludeYearly] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tempData, setTempData] = useState(null);
    const chartRefs = useRef({});

    useEffect(() => {
        if (!includeCharts) {
            setTempData(null);
            return;
        }
        api.get("/haccp/temperatures", { params: { start, end } })
            .then((r) => setTempData(r.data))
            .catch(() => setTempData(null));
    }, [start, end, includeCharts]);

    async function captureCharts() {
        if (!tempData?.frigoriferi) return [];
        const charts = [];
        for (const f of tempData.frigoriferi) {
            const el = chartRefs.current[f.frigo.id];
            if (!el || !f.readings?.length) continue;
            try {
                const dataUrl = await toPng(el, { backgroundColor: "#FFFFFF", pixelRatio: 2, cacheBust: true });
                const b64 = dataUrl.split(",", 2)[1];
                charts.push({
                    title: `${f.frigo.nome} · ${f.count} letture, ${f.breaches} fuori soglia${f.warnings ? `, ${f.warnings} warning` : ""}`,
                    image_base64: b64,
                });
            } catch (e) {
                console.warn("chart capture failed", e);
            }
        }
        return charts;
    }

    async function downloadReport() {
        if (!start || !end) {
            toast.error("Seleziona inizio e fine");
            return;
        }
        setLoading(true);
        try {
            let blob;
            if (includeCharts || includeYearly) {
                let charts = [];
                if (includeCharts) {
                    await new Promise((r) => setTimeout(r, 400));
                    charts = await captureCharts();
                }
                const resp = await fetch(`${API_BASE}/reports/haccp`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        start,
                        end,
                        charts,
                        include_yearly: includeYearly,
                        yearly_year: parseInt(end.slice(0, 4)),
                    }),
                });
                if (!resp.ok) throw new Error("Errore generazione PDF");
                blob = await resp.blob();
            } else {
                const resp = await fetch(`${API_BASE}/reports/haccp?start=${start}&end=${end}`);
                if (!resp.ok) throw new Error("Errore generazione PDF");
                blob = await resp.blob();
            }
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = `report_haccp_${start}_${end}.pdf`;
            link.rel = "noopener";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
            toast.success("Report scaricato");
        } catch (e) {
            toast.error("Errore download PDF");
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="space-y-6 pb-6" data-testid="report-page">
            <section className="card-soft p-6">
                <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-forest text-white grid place-items-center">
                        <FileText size={24} />
                    </div>
                    <div className="flex-1">
                        <h2 className="font-display text-xl font-semibold text-forest-dark">Report HACCP per ASL</h2>
                        <p className="text-sm text-stone-600 mt-1">
                            Genera un PDF con tutte le registrazioni di autocontrollo del periodo selezionato, da presentare in caso di ispezione.
                        </p>
                    </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-3 mt-6">
                    <label className="block">
                        <span className="text-xs font-semibold text-stone-600 inline-flex items-center gap-1">
                            <Calendar size={12} /> Dal
                        </span>
                        <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="input-soft mt-1" data-testid="report-start" />
                    </label>
                    <label className="block">
                        <span className="text-xs font-semibold text-stone-600 inline-flex items-center gap-1">
                            <Calendar size={12} /> Al
                        </span>
                        <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="input-soft mt-1" data-testid="report-end" />
                    </label>
                </div>

                <label className="flex items-start gap-3 mt-5 bg-bone-2 rounded-xl p-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={includeCharts}
                        onChange={(e) => setIncludeCharts(e.target.checked)}
                        className="w-5 h-5 mt-0.5"
                        data-testid="include-charts-toggle"
                    />
                    <div>
                        <p className="font-semibold text-forest-dark text-sm inline-flex items-center gap-2">
                            <ImageIcon size={14} /> Includi grafici temperature
                        </p>
                        <p className="text-xs text-stone-500 mt-0.5">
                            Allega al PDF il grafico di andamento per ciascun frigorifero con la fascia di conformità.
                            {tempData && (
                                <> · {tempData.frigoriferi?.filter((f) => f.readings?.length).length || 0} grafici disponibili</>
                            )}
                        </p>
                    </div>
                </label>

                <label className="flex items-start gap-3 mt-2 bg-bone-2 rounded-xl p-3 cursor-pointer">
                    <input
                        type="checkbox"
                        checked={includeYearly}
                        onChange={(e) => setIncludeYearly(e.target.checked)}
                        className="w-5 h-5 mt-0.5"
                        data-testid="include-yearly-toggle"
                    />
                    <div>
                        <p className="font-semibold text-forest-dark text-sm inline-flex items-center gap-2">
                            <BarChart3 size={14} /> Includi riepilogo annuale spese e incassi
                        </p>
                        <p className="text-xs text-stone-500 mt-0.5">
                            Aggiunge una pagina dedicata con totali annuali, andamento mensile, breakdown per categoria e top fornitori — utile per il commercialista.
                        </p>
                    </div>
                </label>

                <button
                    onClick={downloadReport}
                    disabled={loading}
                    className="btn-primary mt-5 inline-flex items-center gap-2"
                    data-testid="btn-download-report"
                >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                    Scarica report PDF
                </button>
            </section>

            <section className="card-soft p-6 bg-bone-2/50">
                <h3 className="font-display text-lg font-semibold text-forest-dark">Contenuto del report</h3>
                <ul className="mt-3 space-y-2 text-sm text-stone-700">
                    <li>• Anagrafica del negozio (P.IVA, codice fiscale, indirizzo)</li>
                    <li>• Grafici trend temperature per ogni frigorifero {includeCharts && <span className="text-emerald-700 font-semibold">✓ incluso</span>}</li>
                    <li>• Riepilogo annuale spese, incassi, categorie, top fornitori {includeYearly && <span className="text-emerald-700 font-semibold">✓ incluso</span>}</li>
                    <li>• Checklist giornaliera di autocontrollo per ogni giorno</li>
                    <li>• Letture temperature di banco e cella frigorifera</li>
                    <li>• Registrazioni pulizia e sanificazione</li>
                    <li>• Controlli ricevimento merce e scadenze</li>
                    <li>• Non conformità e azioni correttive intraprese</li>
                    <li>• Spazio firma del responsabile HACCP</li>
                </ul>
            </section>

            {/* Hidden charts for PDF capture */}
            {includeCharts && tempData?.frigoriferi?.length > 0 && (
                <div
                    style={{ position: "absolute", left: -10000, top: 0, width: 880, opacity: 0, pointerEvents: "none" }}
                    aria-hidden="true"
                >
                    {tempData.frigoriferi
                        .filter((f) => f.readings?.length)
                        .map((f) => (
                            <div
                                key={f.frigo.id}
                                ref={(el) => {
                                    if (el) chartRefs.current[f.frigo.id] = el;
                                }}
                                style={{ width: 880, padding: 24, background: "#fff" }}
                                data-testid={`hidden-chart-${f.frigo.id}`}
                            >
                                <h3 style={{ color: "#2C4C3B", fontFamily: "Outfit, sans-serif", fontSize: 18, marginBottom: 4 }}>
                                    {f.frigo.nome}
                                </h3>
                                <p style={{ color: "#5C5C5C", fontSize: 11, marginBottom: 16 }}>
                                    Range conformità {f.frigo.min_c}°C — {f.frigo.max_c}°C
                                    {f.frigo.buffer_c > 0 && <> · warning ±{f.frigo.buffer_c}°C</>}
                                    {" · "}{f.count} letture · {f.breaches} fuori soglia
                                </p>
                                <FridgeChart frigo={f.frigo} readings={f.readings} height={300} showTooltip={false} />
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
}
